// Protocolo de Uso — Diagnóstico Kids
// Recurso pós-venda usado pela consultora para orientar a mãe APÓS a aquisição dos óleos.

export type FaixaEtariaKids = 'BEBE' | 'PRE_ESCOLAR' | 'CRIANCA' | 'ADOLESCENTE' | string

export interface RotinaItem {
  periodo: 'MANHA' | 'TARDE' | 'NOITE'
  oleo: string
  nomeExibicao: string
  emoji: string
  locais: string
  finalidade: string
}

export interface ProtocoloOleo {
  chave: string
  nomeExibicao: string
  emoji: string
  categoria: 'mix' | 'touch'
  locaisPadrao: string[]
  rotinas: { titulo: string; texto: string }[]
  frequencia: string
  evitarLuzSolar?: boolean
  // Apenas para adolescentes (com ou sem condição extra)
  apenasAdolescente?: boolean
  // Apenas adolescentes com condição especial (TDAH, autismo, hiperatividade, déficit de atenção)
  apenasAdolescenteEspecial?: boolean
}

// Tabela de locais por mix kids e por faixa etária (especificação oficial).
// Para CRIANCA/ADOLESCENTE usa-se locaisPadrao de cada óleo (definidos na tabela PROTOCOLOS_KIDS).
const LOCAIS_MIX_POR_FAIXA: Record<string, { BEBE: string[]; PRE_ESCOLAR: string[] }> = {
  CALMER: {
    BEBE: ['Sola dos pés', 'Costas'],
    PRE_ESCOLAR: ['Peito', 'Costas', 'Sola dos pés'],
  },
  THINKER: {
    BEBE: ['Sola dos pés', 'Costas'],
    PRE_ESCOLAR: ['Sola dos pés', 'Peito'],
  },
  BRAVE: {
    BEBE: ['Sola dos pés', 'Costas'],
    PRE_ESCOLAR: ['Peito', 'Costas', 'Sola dos pés'],
  },
  STEADY: {
    BEBE: ['Sola dos pés', 'Costas'],
    PRE_ESCOLAR: ['Sola dos pés', 'Peito', 'Costas'],
  },
  STRONGER: {
    BEBE: ['Sola dos pés', 'Costas'],
    PRE_ESCOLAR: ['Peito', 'Costas', 'Sola dos pés'],
  },
  TAMER: {
    BEBE: ['Sola dos pés'],
    PRE_ESCOLAR: ['Barriguinha em movimentos circulares suaves', 'Sola dos pés'],
  },
  RESCUER: {
    BEBE: ['Sola dos pés', 'Costas'],
    PRE_ESCOLAR: ['Peito', 'Costas', 'Sola dos pés'],
  },
}

// Locais especiais por faixa etária — sobrescreve locaisPadrao
function obterLocais(p: ProtocoloOleo, faixa: FaixaEtariaKids): string[] {
  // Mixes kids têm locais específicos por faixa etária conforme especificação
  if (p.categoria === 'mix') {
    const mapa = LOCAIS_MIX_POR_FAIXA[p.chave]
    if (mapa) {
      if (faixa === 'BEBE') return mapa.BEBE
      if (faixa === 'PRE_ESCOLAR') return mapa.PRE_ESCOLAR
      // CRIANCA e ADOLESCENTE usam os locais padrão do mix
      return p.locaisPadrao
    }
  }
  // Óleos touch (ou mix sem mapa): mantém regra geral por faixa
  if (faixa === 'BEBE') {
    return ['Sola dos pés', 'Costas']
  }
  if (faixa === 'PRE_ESCOLAR') {
    return ['Sola dos pés', 'Peito', 'Costas']
  }
  return p.locaisPadrao
}

// Tabela de protocolos
export const PROTOCOLOS_KIDS: Record<string, ProtocoloOleo> = {
  CALMER: {
    chave: 'CALMER',
    nomeExibicao: 'Mix para Serenidade',
    emoji: '🌙',
    categoria: 'mix',
    locaisPadrao: ['Peito', 'Costas', 'Sola dos pés'],
    rotinas: [{ titulo: 'ANTES DE DORMIR', texto: 'Aplicar no peito, costas e sola dos pés durante a rotina do banho ou antes de dormir.' }],
    frequencia: 'Diariamente',
  },
  THINKER: {
    chave: 'THINKER',
    nomeExibicao: 'Mix para Foco',
    emoji: '🧠',
    categoria: 'mix',
    locaisPadrao: ['Nuca', 'Peito', 'Sola dos pés'],
    rotinas: [{ titulo: 'MANHÃ', texto: 'Aplicar na nuca, peito e sola dos pés antes das atividades escolares ou que exijam foco e concentração.' }],
    frequencia: 'Conforme necessidade',
    evitarLuzSolar: true,
  },
  BRAVE: {
    chave: 'BRAVE',
    nomeExibicao: 'Mix para Coragem',
    emoji: '🦁',
    categoria: 'mix',
    locaisPadrao: ['Peito', 'Estômago', 'Costas', 'Sola dos pés'],
    rotinas: [{ titulo: 'MANHÃ ou conforme necessidade', texto: 'Aplicar no peito, estômago, costas e sola dos pés antes de situações que gerem insegurança ou medo.' }],
    frequencia: 'Conforme necessidade',
    evitarLuzSolar: true,
  },
  STEADY: {
    chave: 'STEADY',
    nomeExibicao: 'Mix para Equilíbrio',
    emoji: '⚖️',
    categoria: 'mix',
    locaisPadrao: ['Nuca', 'Peito', 'Sola dos pés'],
    rotinas: [
      {
        titulo: 'CONFORME NECESSIDADE',
        texto: 'Aplicar na nuca, peito e sola dos pés em momentos em que for importante ficar equilibrado.',
      },
    ],
    frequencia: 'Conforme necessidade',
  },
  STRONGER: {
    chave: 'STRONGER',
    nomeExibicao: 'Mix Protetor',
    emoji: '🛡️',
    categoria: 'mix',
    locaisPadrao: ['Peito', 'Abdome', 'Costas', 'Sola dos pés'],
    rotinas: [{ titulo: 'MANHÃ', texto: 'Aplicar no peito, abdome, costas e sola dos pés para proteção diária da imunidade.' }],
    frequencia: 'Diariamente',
  },
  TAMER: {
    chave: 'TAMER',
    nomeExibicao: 'Mix Digestivo',
    emoji: '🌬️',
    categoria: 'mix',
    locaisPadrao: ['Barriga (abdome) em movimentos circulares suaves', 'Sola dos pés'],
    rotinas: [
      {
        titulo: 'APÓS REFEIÇÕES ou conforme desconforto',
        texto: 'Aplicar no abdome em movimentos circulares suaves e na sola dos pés após as refeições ou sempre que houver desconforto digestivo.',
      },
    ],
    frequencia: 'Conforme necessidade',
  },
  RESCUER: {
    chave: 'RESCUER',
    nomeExibicao: 'Mix para Conforto',
    emoji: '💙',
    categoria: 'mix',
    locaisPadrao: ['Nuca', 'Peito', 'Ombros', 'Costas', 'Sola dos pés'],
    rotinas: [
      {
        titulo: 'CONFORME NECESSIDADE',
        texto: 'Aplicar na nuca, peito, ombros, costas e sola dos pés sempre que houver dores musculares, dores de crescimento, dor de cabeça ou desconforto.',
      },
    ],
    frequencia: 'Conforme necessidade',
  },
  // Touch
  LAVANDA_TOUCH: {
    chave: 'LAVANDA_TOUCH',
    nomeExibicao: 'Lavanda Touch',
    emoji: '💜',
    categoria: 'touch',
    locaisPadrao: ['Pulsos', 'Peito', 'Costas', 'Sola dos pés'],
    rotinas: [{ titulo: 'NOITE', texto: 'Aplicar nos pulsos, peito, costas e sola dos pés antes de dormir ou em momentos de ansiedade e agitação.' }],
    frequencia: 'Diariamente ou conforme necessidade',
  },
  VETIVER_TOUCH: {
    chave: 'VETIVER_TOUCH',
    nomeExibicao: 'Vetiver Touch',
    emoji: '🌾',
    categoria: 'touch',
    locaisPadrao: ['Sola dos pés', 'Pulsos', 'Base do pescoço'],
    rotinas: [
      { titulo: 'MANHÃ', texto: 'Aplicar na sola dos pés, pulsos e base do pescoço antes de atividades que exijam foco e concentração.' },
      { titulo: 'NOITE', texto: 'Aplicar na sola dos pés e pulsos antes de dormir para sono mais profundo.' },
    ],
    frequencia: 'Diariamente',
    apenasAdolescenteEspecial: true,
  },
  BREATHE_TOUCH: {
    chave: 'BREATHE_TOUCH',
    nomeExibicao: 'Breathe Touch',
    emoji: '🌬️',
    categoria: 'touch',
    locaisPadrao: ['Peito', 'Costas', 'Sola dos pés'],
    rotinas: [
      {
        titulo: 'MANHÃ e CONFORME NECESSIDADE',
        texto: 'Aplicar no peito, costas e sola dos pés pela manhã e sempre que houver desconforto respiratório, congestão ou tosse.',
      },
    ],
    frequencia: 'Conforme necessidade',
  },
  ON_GUARD_TOUCH: {
    chave: 'ON_GUARD_TOUCH',
    nomeExibicao: 'On Guard Touch',
    emoji: '🛡️',
    categoria: 'touch',
    locaisPadrao: ['Sola dos pés', 'Costas', 'Peito'],
    rotinas: [{ titulo: 'MANHÃ', texto: 'Aplicar na sola dos pés, costas e peito diariamente para proteção e fortalecimento do sistema imune.' }],
    frequencia: 'Diariamente',
    evitarLuzSolar: true,
    apenasAdolescente: true,
  },
  ADAPTIV_TOUCH: {
    chave: 'ADAPTIV_TOUCH',
    nomeExibicao: 'Adaptiv Touch',
    emoji: '🌿',
    categoria: 'touch',
    locaisPadrao: ['Pulsos', 'Pescoço', 'Ombros'],
    rotinas: [{ titulo: 'CONFORME NECESSIDADE', texto: 'Aplicar nos pulsos, pescoço e ombros em momentos de ansiedade, estresse ou pressão escolar.' }],
    frequencia: 'Conforme necessidade',
    evitarLuzSolar: true,
    apenasAdolescente: true,
  },
  HORTELA_TOUCH: {
    chave: 'HORTELA_TOUCH',
    nomeExibicao: 'Hortelã Touch',
    emoji: '🌱',
    categoria: 'touch',
    locaisPadrao: ['Nuca', 'Ombros', 'Costas'],
    rotinas: [
      {
        titulo: 'MANHÃ ou CONFORME NECESSIDADE',
        texto: 'Aplicar na nuca, ombros e costas para foco, energia mental ou alívio de dores de cabeça e tensão.',
      },
    ],
    frequencia: 'Conforme necessidade',
    apenasAdolescente: true,
  },
  ZENGEST_TOUCH: {
    chave: 'ZENGEST_TOUCH',
    nomeExibicao: 'Zengest Touch',
    emoji: '🫧',
    categoria: 'touch',
    locaisPadrao: ['Abdome em movimentos circulares suaves', 'Costas', 'Sola dos pés'],
    rotinas: [
      {
        titulo: 'APÓS REFEIÇÕES ou CONFORME NECESSIDADE',
        texto: 'Aplicar no abdome em movimentos circulares suaves, nas costas e sola dos pés após as refeições ou sempre que houver ansiedade digestiva, desconforto ou intestino nervoso.',
      },
    ],
    frequencia: 'Conforme necessidade',
    apenasAdolescente: true,
  },
}

// Mapeia variantes de nomes (usados em mixTouch/oleosTouch dos leads) para a chave da tabela
const ALIAS_TOUCH: Record<string, string> = {
  'Lavanda Touch': 'LAVANDA_TOUCH',
  'Vetiver Touch': 'VETIVER_TOUCH',
  'Breathe Touch': 'BREATHE_TOUCH',
  'On Guard Touch': 'ON_GUARD_TOUCH',
  'OnGuard Touch': 'ON_GUARD_TOUCH',
  'Adaptiv Touch': 'ADAPTIV_TOUCH',
  'Hortelã Touch': 'HORTELA_TOUCH',
  'Hortela Touch': 'HORTELA_TOUCH',
  'Zengest Touch': 'ZENGEST_TOUCH',
  'ZenGest Touch': 'ZENGEST_TOUCH',
}

export function resolverChave(nome: string): string | null {
  if (!nome) return null
  const upper = nome.toUpperCase().trim()
  if (PROTOCOLOS_KIDS[upper]) return upper
  if (ALIAS_TOUCH[nome]) return ALIAS_TOUCH[nome]
  // Tentativa por variantes de capitalização
  const found = Object.keys(ALIAS_TOUCH).find(k => k.toLowerCase() === nome.toLowerCase())
  if (found) return ALIAS_TOUCH[found]
  return null
}

// Caixa de segurança por faixa etária
export function obterAvisoSeguranca(faixa: FaixaEtariaKids): { tipo: 'amarelo' | 'verde'; icone: string; titulo: string; texto: string; textoResumido: string } {
  if (faixa === 'BEBE') {
    return {
      tipo: 'amarelo',
      icone: '⚠️',
      titulo: 'Segurança para bebês',
      texto: 'Aplicar APENAS na sola dos pés. Nunca aplicar próximo ao rosto, boca ou olhos.',
      textoResumido: 'Aplicar apenas na sola dos pés. Nunca aplicar próximo ao rosto, boca ou olhos.',
    }
  }
  if (faixa === 'PRE_ESCOLAR') {
    return {
      tipo: 'amarelo',
      icone: '⚠️',
      titulo: 'Segurança para pré-escolares',
      texto: 'Aplicar na sola dos pés, peito e costas. Para o Mix Digestivo pode aplicar também na barriguinha em movimentos circulares suaves. Evitar área do rosto e olhos.',
      textoResumido: 'Aplicar na sola dos pés, peito e costas. Evitar rosto e olhos.',
    }
  }
  return {
    tipo: 'verde',
    icone: '✅',
    titulo: 'Aplicação segura',
    texto: 'Aplicar nos locais indicados em cada óleo. Evitar contato com olhos e mucosas.',
    textoResumido: 'Aplicar nos locais indicados. Evitar contato com olhos e mucosas.',
  }
}

export interface ProtocoloItemResolvido {
  chave: string
  nomeExibicao: string
  emoji: string
  categoria: 'mix' | 'touch'
  locais: string[]
  rotinas: { titulo: string; texto: string }[]
  frequencia: string
  evitarLuzSolar: boolean
  finalidadeCurta: string
  // Período sugerido para a rotina consolidada
  periodoSugerido: 'MANHA' | 'TARDE' | 'NOITE'
}

const PERIODO_POR_OLEO: Record<string, 'MANHA' | 'TARDE' | 'NOITE'> = {
  // Mixes
  THINKER: 'MANHA',
  STEADY: 'MANHA',
  STRONGER: 'MANHA',
  TAMER: 'TARDE',
  BRAVE: 'TARDE',
  RESCUER: 'TARDE',
  CALMER: 'NOITE',
  // Touch
  ON_GUARD_TOUCH: 'MANHA',
  VETIVER_TOUCH: 'MANHA',
  HORTELA_TOUCH: 'MANHA',
  BREATHE_TOUCH: 'MANHA',
  ZENGEST_TOUCH: 'TARDE',
  ADAPTIV_TOUCH: 'TARDE',
  LAVANDA_TOUCH: 'NOITE',
}

export const FINALIDADE_CURTA: Record<string, string> = {
  CALMER: 'serenidade e sono',
  THINKER: 'foco e concentração',
  BRAVE: 'coragem e segurança',
  STEADY: 'equilíbrio emocional',
  STRONGER: 'proteção e imunidade',
  TAMER: 'conforto digestivo',
  RESCUER: 'alívio e conforto',
  LAVANDA_TOUCH: 'sono e calma',
  VETIVER_TOUCH: 'foco profundo e sono',
  BREATHE_TOUCH: 'conforto respiratório',
  ON_GUARD_TOUCH: 'proteção imunológica',
  ADAPTIV_TOUCH: 'alívio do estresse',
  HORTELA_TOUCH: 'energia e foco',
  ZENGEST_TOUCH: 'conforto digestivo',
}

export function resolverProtocolos(
  mixesIndicados: string[],
  oleosTouchInd: string[],
  faixa: FaixaEtariaKids,
): ProtocoloItemResolvido[] {
  const resultado: ProtocoloItemResolvido[] = []
  const lista: string[] = []
  mixesIndicados.forEach(m => {
    const chave = resolverChave(m)
    if (chave && !lista.includes(chave)) lista.push(chave)
  })
  oleosTouchInd.forEach(o => {
    const chave = resolverChave(o)
    if (chave && !lista.includes(chave)) lista.push(chave)
  })

  lista.forEach(chave => {
    const p = PROTOCOLOS_KIDS[chave]
    if (!p) return
    // Filtros de adolescente
    if (p.apenasAdolescente && faixa !== 'ADOLESCENTE') return
    if (p.apenasAdolescenteEspecial && faixa !== 'ADOLESCENTE') return

    resultado.push({
      chave,
      nomeExibicao: p.nomeExibicao,
      emoji: p.emoji,
      categoria: p.categoria,
      locais: obterLocais(p, faixa),
      rotinas: p.rotinas,
      frequencia: p.frequencia,
      evitarLuzSolar: !!p.evitarLuzSolar,
      finalidadeCurta: FINALIDADE_CURTA[chave] || '',
      periodoSugerido: PERIODO_POR_OLEO[chave] || 'TARDE',
    })
  })

  return resultado
}

// Um óleo entra em "Conforme necessidade" quando a rotina dele é exatamente esse título
// ("ANTES DE DORMIR" continua sendo tratado como Noite).
function ehConformeNecessidade(item: ProtocoloItemResolvido): boolean {
  return item.rotinas.some(r => r.titulo.trim().toUpperCase() === 'CONFORME NECESSIDADE')
}

// Gera a rotina consolidada por período
export function gerarRotinaConsolidada(itens: ProtocoloItemResolvido[]) {
  const manha: ProtocoloItemResolvido[] = []
  const tarde: ProtocoloItemResolvido[] = []
  const noite: ProtocoloItemResolvido[] = []
  const conformeNecessidade: ProtocoloItemResolvido[] = []
  itens.forEach(i => {
    if (ehConformeNecessidade(i)) {
      conformeNecessidade.push(i)
      return
    }
    if (i.periodoSugerido === 'MANHA') manha.push(i)
    else if (i.periodoSugerido === 'TARDE') tarde.push(i)
    else noite.push(i)
  })
  return { manha, tarde, noite, conformeNecessidade }
}

// ══════════════════════════════════════════
// Geradores de texto para WhatsApp
// ══════════════════════════════════════════

// BOTÃO 1 — Protocolo dos mixes (apenas mixes kids, sem touch)
export function gerarTextoProtocoloMixes(
  nomeCrianca: string,
  faixa: FaixaEtariaKids,
  itens: ProtocoloItemResolvido[],
): string {
  const aviso = obterAvisoSeguranca(faixa)
  const mixes = itens.filter(i => i.categoria === 'mix')

  const linhas: string[] = [
    `🌿 *Protocolo dos Mixes — ${nomeCrianca}*`,
    '',
    `⚠️ ${aviso.textoResumido}`,
    '',
  ]

  mixes.forEach(item => {
    linhas.push(`*${item.emoji} ${item.nomeExibicao}:*`)
    linhas.push(`📍 Locais: ${item.locais.join(', ')}`)
    linhas.push(`🕐 Quando: ${item.rotinas[0]?.titulo || 'Conforme necessidade'}`)
    linhas.push(`📆 Frequência: ${item.frequencia}`)
    if (item.evitarLuzSolar) linhas.push('⚠️ Evitar luz solar por 12h após aplicação')
    linhas.push('')
  })

  linhas.push('Qualquer dúvida estou à disposição! 🌿')
  return linhas.join('\n')
}

// BOTÃO 2 — Rotina de bem-estar (manhã/tarde/noite de todos os itens)
export function gerarTextoRotinaBeEstar(
  nomeCrianca: string,
  faixa: FaixaEtariaKids,
  itens: ProtocoloItemResolvido[],
): string {
  const aviso = obterAvisoSeguranca(faixa)
  const { manha, tarde, noite, conformeNecessidade } = gerarRotinaConsolidada(itens)

  const linhasPeriodo = (lista: ProtocoloItemResolvido[]): string => {
    if (lista.length === 0) return '→ (sem aplicações neste período)'
    return lista
      .map(i => {
        const locais = i.locais.join(', ').toLowerCase()
        const sol = i.evitarLuzSolar ? ' (evitar luz solar por 12h)' : ''
        return `→ ${i.emoji} ${i.nomeExibicao}: aplicar em ${locais}${sol}`
      })
      .join('\n')
  }

  const blocos = [
    `🌿 *Rotina de Bem-estar de ${nomeCrianca}*`,
    '',
    `⚠️ ${aviso.textoResumido}`,
    '',
    '*☀️ MANHÃ:*',
    linhasPeriodo(manha),
    '',
    '*🌤️ TARDE:*',
    linhasPeriodo(tarde),
    '',
    '*🌙 NOITE:*',
    linhasPeriodo(noite),
    '',
  ]

  if (conformeNecessidade.length > 0) {
    blocos.push('*🔁 CONFORME NECESSIDADE:*')
    blocos.push(linhasPeriodo(conformeNecessidade))
    blocos.push('')
  }

  blocos.push('Qualquer dúvida estou à disposição! 🌿')
  return blocos.join('\n')
}

// BLOCO B — Protocolo Touch
export function gerarTextoProtocoloTouch(
  nomeCrianca: string,
  itens: ProtocoloItemResolvido[],
): string {
  const touchs = itens.filter(i => i.categoria === 'touch')

  const linhas: string[] = [
    `🌿 *Protocolo Touch — ${nomeCrianca}*`,
    '',
    '💡 Os óleos touch são diluídos e prontos para usar diretamente na pele. Também podem ser usados por toda a família.',
    '',
  ]

  touchs.forEach(item => {
    linhas.push(`*${item.emoji} ${item.nomeExibicao}:*`)
    linhas.push(`📍 Locais: ${item.locais.join(', ')}`)
    linhas.push(`🕐 Quando: ${item.rotinas[0]?.titulo || 'Conforme necessidade'}`)
    linhas.push(`📆 Frequência: ${item.frequencia}`)
    if (item.evitarLuzSolar) linhas.push('⚠️ Evitar luz solar por 12h após aplicação')
    linhas.push('')
  })

  linhas.push('Qualquer dúvida estou à disposição! 🌿')
  return linhas.join('\n')
}

// BLOCO C — Protocolo Personalizado
export function gerarTextoProtocoloPersonalizado(
  nomeCrianca: string,
  faixa: FaixaEtariaKids,
  itens: ProtocoloItemResolvido[],
): string {
  const aviso = obterAvisoSeguranca(faixa)
  const mixes = itens.filter(i => i.categoria === 'mix')
  const touchs = itens.filter(i => i.categoria === 'touch')
  const { manha, tarde, noite, conformeNecessidade } = gerarRotinaConsolidada(itens)

  const linhas: string[] = [
    `🌿 *Protocolo Personalizado — ${nomeCrianca}*`,
    '',
    `⚠️ ${aviso.textoResumido}`,
    '',
  ]

  if (mixes.length > 0) {
    linhas.push('*Mixes:*')
    linhas.push('')
    mixes.forEach(item => {
      linhas.push(`*${item.emoji} ${item.nomeExibicao}:*`)
      linhas.push(`📍 Locais: ${item.locais.join(', ')}`)
      linhas.push(`🕐 Quando: ${item.rotinas[0]?.titulo || 'Conforme necessidade'}`)
      linhas.push(`📆 Frequência: ${item.frequencia}`)
      if (item.evitarLuzSolar) linhas.push('⚠️ Evitar luz solar por 12h após aplicação')
      linhas.push('')
    })
  }

  if (touchs.length > 0) {
    linhas.push('*Óleos Touch:*')
    linhas.push('')
    touchs.forEach(item => {
      linhas.push(`*${item.emoji} ${item.nomeExibicao}:*`)
      linhas.push(`📍 Locais: ${item.locais.join(', ')}`)
      linhas.push(`🕐 Quando: ${item.rotinas[0]?.titulo || 'Conforme necessidade'}`)
      linhas.push(`📆 Frequência: ${item.frequencia}`)
      if (item.evitarLuzSolar) linhas.push('⚠️ Evitar luz solar por 12h após aplicação')
      linhas.push('')
    })
  }

  const linhasPeriodo = (lista: ProtocoloItemResolvido[]): string => {
    if (lista.length === 0) return '→ (sem aplicações neste período)'
    return lista
      .map(i => {
        const locais = i.locais.join(', ').toLowerCase()
        const sol = i.evitarLuzSolar ? ' (evitar luz solar por 12h)' : ''
        return `→ ${i.emoji} ${i.nomeExibicao}: aplicar em ${locais}${sol}`
      })
      .join('\n')
  }

  linhas.push('*☀️ ROTINA SUGERIDA:*')
  linhas.push('')
  linhas.push('*☀️ MANHÃ:*')
  linhas.push(linhasPeriodo(manha))
  linhas.push('')
  linhas.push('*🌤️ TARDE:*')
  linhas.push(linhasPeriodo(tarde))
  linhas.push('')
  linhas.push('*🌙 NOITE:*')
  linhas.push(linhasPeriodo(noite))
  linhas.push('')
  if (conformeNecessidade.length > 0) {
    linhas.push('*🔁 CONFORME NECESSIDADE:*')
    linhas.push(linhasPeriodo(conformeNecessidade))
    linhas.push('')
  }
  linhas.push('Qualquer dúvida estou à disposição! 🌿')

  return linhas.join('\n')
}

// Mantido para compatibilidade legada (usado em referências antigas)
export function gerarTextoProtocoloWhatsApp(
  nomeCrianca: string,
  faixa: FaixaEtariaKids,
  itens: ProtocoloItemResolvido[],
): string {
  return gerarTextoProtocoloPersonalizado(nomeCrianca, faixa, itens)
}
